<script setup lang="ts">
/**
 * HLOS-138 — ConversationChat (UI do chat).
 *
 * Renderiza histórico, iniciativa e input do learner. O adapter de turno
 * vive em `useConversationTurns` (HLOS-140); status (loading/error) vive
 * em `ConversationStatus` (compartilhado com o Init).
 *
 * **HLOS-255 — LX Layer integração UX end-to-end:**
 * Inclui `<LXShell>` quando há LX ativa + disclosure para LX proposta
 * (Princípio V — Learner Decide) + LGPD disclosure + delete button.
 *
 * **Constitucional:** R1-R6 (CL-06) via InitiativeBadge/Actions · CC-04
 * (zero "digitando..." artificial) · Princípio XI (role 'companion', zero
 * menção LLM) · CL-07 (zero rede) · CL-08 (LGPD disclosure + delete).
 *
 * **Ref:** HLOS-140 (split), HLOS-138, HLOS-38, HLOS-15, HLOS-249
 * (Vue renderers), HLOS-253 (Gap C LGPD), HLOS-254 (Pinia bridge), HLOS-255
 * (UX integration neste arquivo).
 */
import { computed, ref, watch } from "vue";
import { useConversation } from "../composables/useConversation";
import { useCompanion } from "../../companion/composables/useCompanion";
import { useInitiative } from "../../initiative/composables/useInitiative";
import { useConversationTurns } from "../composables/useConversationTurns";
import { useLXTrigger } from "../../lx/composables/useLXTrigger";
import { shouldUseMock } from "../../pyodide/bridge";
import MessageList from "./MessageList.vue";
import TurnInput from "./TurnInput.vue";
import InitiativeBadge from "../../initiative/components/InitiativeBadge.vue";
import InitiativeActions from "../../initiative/components/InitiativeActions.vue";
import ConversationStatus from "./ConversationStatus.vue";
import LXShell from "../../lx/components/LXShell.vue";

const emit = defineEmits<{
  (e: "end"): void;
}>();

const conv = useConversation();
const initiative = useInitiative();
const companion = useCompanion();
const { onSubmit } = useConversationTurns();

/** HLOS-323: ref ao TurnInput para resetar o flag de Vision analyzing. */
const turnInputRef = ref<{ resetAnalyzing: () => void } | null>(null);

/** Quando o companion volta a idle (turno terminou), resetamos o flag. */
watch(
  () => companion.state.value,
  (s) => {
    if (s === "idle") {
      turnInputRef.value?.resetAnalyzing();
    }
  },
);

/** HLOS-255 — LX UX trigger (disclosure, accept, decline, abandon, delete). */
const lx = useLXTrigger();

/** HLOS-38: loading via orchestrator ('thinking'/'responding'). */
const isKernelLoading = computed<boolean>(
  () => companion.state.value !== "idle",
);
const isKernelErrored = computed<boolean>(
  () => companion.lastError.value !== null,
);

function onEnd(): void {
  emit("end");
}
</script>

<template>
  <section data-testid="conversation-chat">
    <!-- HLOS-45 v0.4.4: badge de modo Pyodide (debug). Tokens HLOS-91/74. -->
    <small
      class="hlos-conversation__mode-badge"
      data-testid="pyodide-mode-badge"
      aria-label="Modo Pyodide ativo"
    >
      Modo Pyodide:
      <strong>{{ shouldUseMock() ? "MOCK (offline)" : "REAL (CDN)" }}</strong>
    </small>

    <header class="hlos-conversation__header">
      <h2>Em conversa</h2>
      <button
        type="button"
        data-testid="conversation-end"
        @click="onEnd"
      >
        Encerrar e voltar
      </button>
    </header>

    <MessageList :messages="conv.messages.value" />

    <!--
      HLOS-255 — LX Proposal Disclosure (Princípio V — Learner Decide).
      Antes de executar qualquer LX, mostra disclosure com rationale.
      Usuário pode aceitar ou recusar; recusar NÃO penaliza θ_lat (CC-05).
      Copy em PT-BR neutra, zero dark patterns (CC-08).
    -->
    <aside
      v-if="lx.isProposalVisible.value"
      class="hlos-conversation__lx-disclosure"
      data-testid="lx-proposal-disclosure"
      aria-live="polite"
    >
      <h3>Aprendizado interativo disponível</h3>
      <p data-testid="lx-proposal-rationale">
        {{ lx.currentProposal.value?.rationale || "O Companheiro sugere explorar isso juntos." }}
      </p>
      <p class="hlos-conversation__lx-disclosure-meta">
        <small>
          Tipo: <strong>{{ lx.currentProposal.value?.lx_type }}</strong>
          · Duração estimada: ~{{ lx.currentProposal.value?.estimated_duration_sec ?? 60 }}s
        </small>
      </p>
      <div class="hlos-conversation__lx-disclosure-actions">
        <button
          type="button"
          data-testid="lx-accept"
          :disabled="lx.isLoading.value"
          @click="async () => {
            if (lx.currentProposal.value) {
              await lx.onAcceptLX(
                lx.currentProposal.value.lx_type,
                lx.currentProposal.value.rationale,
              );
            }
          }"
        >
          Aceitar
        </button>
        <button
          type="button"
          data-testid="lx-decline"
          :disabled="lx.isLoading.value"
          @click="lx.onDeclineLX"
        >
          Recusar
        </button>
      </div>
    </aside>

    <!--
      HLOS-255 — LX Shell ativo (quando há LX rodando).
      Renderers lazy-loaded via defineAsyncComponent (HLOS-249 code-split).
      A11y Honrado (HLOS-249): aria-live + keyboard nav + reduced motion.
    -->
    <LXShell
      v-if="lx.currentLX.value"
      :lx="lx.currentLX.value"
      data-testid="conversation-lx-shell"
      @interaction="(event) => lx.currentLX.value && lx.onLXInteraction(lx.currentLX.value.instance_id, event)"
      @abandon="(instanceId) => lx.onLXAbandon(instanceId)"
      @complete="(state) => lx.onLXComplete(state)"
    />

    <!--
      R1-R6: badge visível APENAS quando há iniciativa ativa (R6 — HLOS-19).
      HLOS-40: botões R3/R4/R5 — affordance de override (Princípio V, CC-05).
    -->
    <InitiativeBadge
      v-if="initiative.hasInitiative.value && initiative.currentOrNull.value"
      :initiative="initiative.currentOrNull.value"
    />
    <InitiativeActions />

    <!--
      HLOS-253 (Gap C) + HLOS-255 — LGPD disclosure + delete UX.
      Honra LGPD Art. 18 (right to be forgotten) + CL-08 (Ética fechada).
      Zero penalidade — deletar LX state NÃO afeta θ_lat (CC-05).
    -->
    <aside
      class="hlos-conversation__lgpd"
      data-testid="lx-lgpd-disclosure"
      aria-live="polite"
    >
      <p>
        <strong>Privacidade:</strong> o estado das atividades interativas fica
        apenas no seu dispositivo. Você pode apagar quando quiser.
      </p>
      <button
        type="button"
        data-testid="lx-delete-state"
        :disabled="lx.isLoading.value"
        @click="lx.onDeleteLXState"
      >
        Apagar estado das atividades
      </button>
    </aside>

    <!-- HLOS-15: status runtime (loading durante turno + error fallback). -->
    <ConversationStatus
      :loading="isKernelLoading"
      :errored="isKernelErrored"
      @end="onEnd"
    />

    <TurnInput
      ref="turnInputRef"
      @submit="onSubmit"
      :disabled="isKernelErrored"
    />
  </section>
</template>

<style src="./conversation-chat.css" scoped></style>